import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import { CalendarIcon, CreditCardIcon, ArrowRightIcon } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cancelMemberBooking, getMemberBookings, getUserSubscriptionStatus } from '@/lib/api'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import {
    formatWeeklyAllowance,
    isRecurringSubscription,
} from '@/lib/subscriptions'
import type { Booking, UserSubscription } from '@/types/domain'
import {
    SessionDetailDialog,
    type SessionDetailData,
} from '@/components/session-detail-dialog'

function bookingToSessionDetail(booking: Booking): SessionDetailData {
    const cs = booking.course_sessions
    const availableSpots = cs ? cs.max_participants - cs.confirmed_count : null
    return {
        courseName: cs?.courses?.name ?? 'Kurs',
        courseDescription: cs?.courses?.description ?? null,
        startsAt: cs?.starts_at ?? '',
        endsAt: cs?.ends_at ?? '',
        trainerName: cs?.trainer_name ?? null,
        trainerId: cs?.trainer_id ?? null,
        availableSpots,
        maxParticipants: cs?.max_participants ?? null,
        status: booking.status === 'confirmed' ? 'confirmed'
            : 'cancelled',
        registrationUntil: cs?.registration_until ?? null,
        cancellationUntil: cs?.cancellation_until ?? null,
        minParticipants: cs?.min_participants ?? null,
        confirmedCount: cs?.confirmed_count ?? null,
    }
}

function SubscriptionCard({ sub }: { sub: UserSubscription | null }) {
    if (!sub) {
        return (
            <Card className="border-dashed border-2 border-muted-foreground/30">
                <CardContent className="flex flex-col items-center justify-center gap-4 py-8">
                    <CreditCardIcon className="h-10 w-10 text-muted-foreground/50" />
                    <p className="text-sm text-muted-foreground">Kein aktives Abo vorhanden</p>
                    <Button asChild>
                        <Link to="/abo">Kurspaket wählen</Link>
                    </Button>
                </CardContent>
            </Card>
        )
    }

    const isRecurring = isRecurringSubscription(sub)
    const isCreditBalance = !isRecurring && sub.remaining_credits > 0

    return (
        <Card className="bg-gradient-to-br from-primary/10 via-background to-background border-primary/20">
            <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                    <CreditCardIcon className="h-5 w-5 text-primary" />
                    Dein Abo
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-primary">{sub.package_name}</span>
                    <Badge variant={isCreditBalance ? 'secondary' : sub.is_active ? 'default' : 'secondary'}>
                        {isCreditBalance ? 'Guthaben' : sub.is_active ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                </div>

                {isRecurring ? (
                    <>
                        <p className="text-sm text-muted-foreground">
                            {formatWeeklyAllowance(sub)}
                        </p>
                        {sub.remaining_credits > 0 && (
                            <p className="text-sm text-muted-foreground">
                                Zusätzliches Guthaben:{' '}
                                <span className="font-semibold text-foreground">{sub.remaining_credits} Kurse</span>
                            </p>
                        )}
                    </>
                ) : (
                    <p className="text-sm text-muted-foreground">
                        Verbleibendes Guthaben: <span className="font-semibold text-foreground">{sub.remaining_credits} Kurse</span>
                    </p>
                )}

                {sub.expires_at && (
                    <p className="text-xs text-muted-foreground">
                        Gültig bis: {format(new Date(sub.expires_at), 'dd.MM.yyyy', { locale: de })}
                    </p>
                )}
                {sub.stripe_cancel_at_period_end && sub.current_period_end && (
                    <p className="text-xs text-amber-600">
                        Kündigung geplant zum {format(new Date(sub.current_period_end), 'dd.MM.yyyy', { locale: de })}.
                    </p>
                )}

                <Button asChild variant="outline" size="sm" className="mt-2">
                    <Link to="/abo">Details & Upgrade</Link>
                </Button>
            </CardContent>
        </Card>
    )
}

export function MemberDashboardPage() {
    const queryClient = useQueryClient()
    const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null)

    const cancelMutation = useMutation({
        mutationFn: cancelMemberBooking,
        onSuccess: () => {
            toast.success('Buchung erfolgreich storniert.')
            setSelectedBooking(null)
            queryClient.invalidateQueries({ queryKey: queryKeys.memberBookings })
            queryClient.invalidateQueries({ queryKey: queryKeys.memberBookingsUpcoming })
        },
        onError: (error) => {
            toast.error(getErrorMessage(error))
        },
    })

    const subscriptionQuery = useQuery({
        queryKey: queryKeys.userSubscription,
        queryFn: getUserSubscriptionStatus,
        retry: false,
    })

    const bookingsQuery = useQuery({
        queryKey: queryKeys.memberBookingsUpcoming,
        queryFn: () => getMemberBookings({ upcomingOnly: true }),
        retry: false,
    })

    const upcomingBookings = (bookingsQuery.data ?? [])
        .filter((b) => b.status === 'confirmed' && b.course_sessions)
        .sort((a, b) => {
            const aDate = a.course_sessions?.starts_at ?? ''
            const bDate = b.course_sessions?.starts_at ?? ''
            return aDate.localeCompare(bDate)
        })
        .slice(0, 5)

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Übersicht</h1>
                <p className="text-sm text-muted-foreground">
                    Willkommen zurück! Hier siehst du dein Abo und deine nächsten Kurse.
                </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
                {subscriptionQuery.isLoading ? (
                    <Card>
                        <CardContent className="py-8">
                            <Skeleton className="h-24 w-full" />
                        </CardContent>
                    </Card>
                ) : (
                    <SubscriptionCard sub={subscriptionQuery.data ?? null} />
                )}

                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <CalendarIcon className="h-5 w-5 text-primary" />
                            Nächste Kurse
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {bookingsQuery.isLoading ? (
                            <div className="space-y-2">
                                <Skeleton className="h-12 w-full" />
                                <Skeleton className="h-12 w-full" />
                                <Skeleton className="h-12 w-full" />
                            </div>
                        ) : upcomingBookings.length === 0 ? (
                            <div className="flex flex-col items-center gap-3 py-6">
                                <CalendarIcon className="h-8 w-8 text-muted-foreground/50" />
                                <p className="text-sm text-muted-foreground">Keine kommenden Kurse</p>
                                <Button asChild variant="outline" size="sm">
                                    <Link to="/kurse">Kurse entdecken</Link>
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {upcomingBookings.map((booking) => (
                                    <div
                                        key={booking.id}
                                        className="flex items-center justify-between rounded-lg border p-3 transition hover:bg-muted/50 cursor-pointer"
                                        onClick={() => setSelectedBooking(booking)}
                                    >
                                        <div>
                                            <p className="font-medium text-sm">
                                                {booking.course_sessions?.courses?.name ?? 'Kurs'}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {booking.course_sessions?.starts_at
                                                    ? format(new Date(booking.course_sessions.starts_at), "EEEE, dd.MM. 'um' HH:mm", { locale: de })
                                                    : '-'}
                                            </p>
                                        </div>
                                        <Badge variant="outline" className="text-xs">Gebucht</Badge>
                                    </div>
                                ))}
                                <Button asChild variant="ghost" size="sm" className="w-full mt-2">
                                    <Link to="/meine-kurse" className="flex items-center gap-1">
                                        Alle anzeigen <ArrowRightIcon className="h-3.5 w-3.5" />
                                    </Link>
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            <SessionDetailDialog
                open={selectedBooking !== null}
                onOpenChange={(open) => {
                    if (!open) setSelectedBooking(null)
                }}
                session={selectedBooking ? bookingToSessionDetail(selectedBooking) : null}
                title="Kursdetails"
                description="Informationen zu deinem gebuchten Kurs."
                onCancel={selectedBooking ? () => cancelMutation.mutate(selectedBooking.id) : undefined}
                cancelPending={cancelMutation.isPending}
            />
        </div>
    )
}
